import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { Box, LoadingOverlay, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { AppRoutes } from '@/router/routes.ts';

import { referencesApi } from '@/shared/api/references.ts';
import { getImageScale } from '@/shared/helpers/utils';
import useAppStore from '@/shared/store/app.store';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

type HelpItemProps = {
	referenceId?: number | null;
	isOpen?: boolean;
	close?: () => void;
};

const HelpItem: React.FC<HelpItemProps> = ({ referenceId, isOpen, close }) => {
	const { id } = useParams();

	const [openedImg, { close: closeImg, open: openImg }] = useDisclosure(false);

	const isMobile = useAppStore((state) => state.isMobile);

	const [currentImg, setCurrentImg] = useState<HTMLImageElement | null>(null);
	const [initialScale, setInitialScale] = useState(0);

	const { data: item = null, isLoading } = useQuery({
		queryKey: ['helpItem', id, referenceId],
		queryFn: () => referencesApi.getReferenceById(Number(id ?? referenceId)),
		enabled: (isMobile && !!referenceId) || !!id,
	});

	const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		const elem = event.target as HTMLImageElement;

		if (elem.tagName === 'IMG') {
			setCurrentImg(elem);
			openImg();
		}
	};

	useEffect(() => {
		const img = new Image();

		const params = {
			width: 500,
			height: 500,
		};

		img.src = currentImg?.src ?? '';
		img.onload = () => {
			params.width = img.width;
			params.height = img.height;

			setInitialScale(
				getImageScale(params.width, params.height, document.body.offsetWidth - 32, 350),
			);
		};
	}, [currentImg]);

	if (isMobile)
		return (
			<>
				<BottomSheet
					opened={isOpen!}
					onClose={close!}
					title={item?.name ?? ''}
					withCloseButton={false}
					withBackButton
					isFullScreen
				>
					<Box pos="relative" h="100%" style={{ overflow: 'hidden' }}>
						<LoadingOverlay
							visible={isLoading}
							zIndex={1000}
							overlayProps={{ radius: 2, blur: 2 }}
						/>
						<Box
							style={{ overflowY: 'auto' }}
							h="100%"
							pr="md"
							onClick={handleClick}
							dangerouslySetInnerHTML={{
								__html: String(item?.content.replaceAll('<img', '<img style="max-width: 100%"')),
							}}
						></Box>
					</Box>
				</BottomSheet>

				{currentImg && initialScale && (
					<Modal
						styles={{
							header: {
								backgroundColor: 'transparent',
							},
							content: { backgroundColor: 'transparent', boxShadow: 'none', height: 'max-content' },
							close: { color: 'white' },
						}}
						opened={openedImg}
						onClose={() => {
							closeImg();
							setCurrentImg(null);
							setInitialScale(0);
						}}
						withCloseButton
						centered
					>
						<TransformWrapper
							initialScale={initialScale}
							minScale={0.01}
							maxScale={100}
							centerOnInit
						>
							<TransformComponent
								contentStyle={{
									transform: `translate3d(50%, 1px, 0px) scale(1)`,
								}}
								wrapperStyle={{
									width: '100%',
									height: '100%',
								}}
							>
								<img src={currentImg.src} alt="Изображение" />
							</TransformComponent>
						</TransformWrapper>
					</Modal>
				)}
			</>
		);

	if (!id) return <Navigate to={AppRoutes.Help} replace />;

	return (
		<LayoutRow
			title={{
				title: { children: `${item?.name ?? ''}`, size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)' }}
		>
			<Box pos="relative" h="100%" style={{ overflow: 'hidden' }}>
				<LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 2, blur: 2 }} />
				<Box
					style={{ overflowY: 'auto' }}
					h="100%"
					pr="md"
					dangerouslySetInnerHTML={{ __html: String(item?.content) }}
				></Box>
			</Box>
		</LayoutRow>
	);
};

export default HelpItem;
